import {Component, EventEmitter, Input, OnInit, Output} from "@angular/core";
import {CustomValidators} from "../../../core/helpers/custom-validators";
import {FormControl, FormGroup, ValidatorFn, Validators} from "@angular/forms";
import {NotificationService} from "../../../core/services/notification.service";
import {monthVector, yearVector} from "../../../core/helpers/calendar-data";
import {AccountControllerService} from "../../../core/api/controllers/account-controller/account-controller.service";
import {ValidationHelperService} from "../../../core/helpers/validation-helper.service";
import {EAttemptPaymentStatus} from "../../enums/EAttemptPaymentStatus";
import {Router} from "@angular/router";
import {SettingsService} from "../../../core/services/settings.service";
import {ESubscriptionStatus} from "../../enums/ESubscriptionStatus";
import {AuthService} from "../../../core/services/auth.service";
import {IUpdatePaymentInfoRequest} from "../../../core/api/controllers/account-controller/interfaces/update-payment-info.request";
import {IAttemptPaymentRequest} from "../../../core/api/controllers/account-controller/interfaces/attempt-payment.request";
import {IAttemptPaymentResponse} from "../../../core/api/controllers/account-controller/interfaces/attempt-payment.response";
import {paymentMethodUnavailableMessage, requiresPaymentMethodUpdate} from "../../../core/helpers/payment-failure.helper";

export interface IPaymentInformationForm {
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  cardNumber: FormControl<string>;
  CVV: FormControl<string>;
  selectedYear: FormControl<number>;
  selectedMonth: FormControl<number>;
}

@Component({
  selector: "app-update-payment-info-dialog",
  templateUrl: "./update-payment-info-dialog.component.html",
  styleUrls: ["./update-payment-info-dialog.component.scss"],
})
export class UpdatePaymentInfoDialogComponent implements OnInit {
  @Input() mode: "default" | "subscriptionUpgrade" = "default";
  @Input() title = "Update payment info";
  @Input() attemptPaymentPayload: IAttemptPaymentRequest = {};
  @Input() hasPaymentMethod = true;
  @Input() disabled = false;
  @Output() paymentSucceeded = new EventEmitter<void>();
  @Output() paymentFailed = new EventEmitter<IAttemptPaymentResponse>();
  @Output() paymentMethodSaved = new EventEmitter<void>();
  @Output() busyChange = new EventEmitter<boolean>();

  useMonthVector = false;
  currentMonthVector: number[];
  yearVector: number[];
  isLoading = false;
  paymentInformationForm = new FormGroup({
    firstName: new FormControl<string>("", [
      Validators.required, CustomValidators.nameValidator, Validators.maxLength(50),
    ]),
    lastName: new FormControl<string>("", [Validators.required, CustomValidators.nameValidator, Validators.maxLength(50)]),
    cardNumber: new FormControl<string>("", [Validators.required, CustomValidators.cardValidator, CustomValidators.digitsOnlyValidator,  Validators.maxLength(20)]),
    CVV: new FormControl("", [Validators.required,  CustomValidators.digitsOnlyValidator]),
    selectedYear: new FormControl<number>(null, [Validators.required]),
    selectedMonth: new FormControl<number>(null, [Validators.required])
  }, [CustomValidators.paymentInformationValidator as ValidatorFn]);


  constructor(
    private notificationService: NotificationService,
    private accountControllerService: AccountControllerService,
    private router: Router,
    private settingsService: SettingsService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.initDialog();
  }

  initDialog(): void {
    this.yearVector = yearVector();
    this.currentMonthVector = Object.assign([], monthVector.slice((new Date()).getMonth()), monthVector.length);
    this.paymentInformationForm.controls.selectedMonth.setValue(this.currentMonthVector[0]);
    this.paymentInformationForm.controls.selectedYear.setValue(this.yearVector[0]);
  }

  saveAndRetryClick(): void {
    this.paymentInformationForm.markAllAsTouched();
    if(this.paymentInformationForm.invalid) {
      return;
    }
    this.saveAndRetry();
  }

  saveAndRetry(): void {
    if (this.isLoading || this.disabled) {
      return;
    }

    this.setLoading(true);
    this.accountControllerService
        .updatePaymentInfo(this.createPayload())
        .subscribe(
            () => {
              this.hasPaymentMethod = true;
              this.paymentMethodSaved.emit();
              this.notificationService.success("The payment information has been saved successfully!");
              this.attemptPayment();
            },
            (error) => {
              this.setLoading(false);
              this.notificationService.error(error);
            },
        );
  }


  retryPayment(): void {
    if (this.isLoading || this.disabled || (this.mode === "subscriptionUpgrade" && !this.hasPaymentMethod)) {
      return;
    }

    this.setLoading(true);
    this.attemptPayment();
  }

  private attemptPayment(): void {
    this.accountControllerService.attemptPayment(this.attemptPaymentPayload).subscribe({
      next: (res) => {
        if(res.status === EAttemptPaymentStatus.Paid || (this.mode === "subscriptionUpgrade" && res.status === EAttemptPaymentStatus.NotRequired)) {
          this.notificationService.success("The payment has been executed succesfully!");
          if(this.mode === "subscriptionUpgrade") {
            this.paymentSucceeded.emit();
            this.setLoading(false);
            return;
          }
          if(this.settingsService.settings.subscription.subscriptionStatus === ESubscriptionStatus.Inactive) this.authService.logout();
        }
        else if(res.status === EAttemptPaymentStatus.NotRequired) this.notificationService.warn("The payment was not required.")
        else {
          this.handlePaymentFailure(res);
        }
        this.setLoading(false);
        if(this.mode === "default") this.cancel();
      },
      error: (res) => {
        this.handlePaymentFailure(res?.error);
        this.setLoading(false);
      }
    })
  }

  private handlePaymentFailure(response?: IAttemptPaymentResponse): void {
    const needsCard = this.mode === "subscriptionUpgrade" && requiresPaymentMethodUpdate(response?.failureReason);
    if (needsCard) {
      this.hasPaymentMethod = false;
    }
    this.notificationService.error(needsCard ? paymentMethodUnavailableMessage : "The payment has failed.");
    this.paymentFailed.emit(response ?? {status: EAttemptPaymentStatus.Failed});
  }

  private setLoading(isLoading: boolean): void {
    this.isLoading = isLoading;
    this.busyChange.emit(isLoading);
  }

  validateDate() {
    if (this.paymentInformationForm.controls.selectedYear.value !== this.yearVector[0]) {
      this.useMonthVector = true;
    } else {
      this.useMonthVector = false;
      this.paymentInformationForm.controls.selectedMonth.setValue(this.currentMonthVector[0]);
    }
  }

  onSubmit() {
    if (this.mode === "subscriptionUpgrade") {
      this.saveAndRetryClick();
      return;
    }

    this.paymentInformationForm.markAllAsTouched();
    if(this.paymentInformationForm.invalid) {
      return;
    }
    this.updatePaymentInformation();
  }

  updatePaymentInformation() {
    if (this.isLoading || this.disabled) {
      return;
    }

    this.setLoading(true);
    this.accountControllerService
        .updatePaymentInfo(this.createPayload())
        .subscribe(
            () => {
              this.setLoading(false);
              this.notificationService.success("The payment information has been updated succesfully!");
              this.cancel();
            },
            (error) => {
              this.setLoading(false);
              this.notificationService.error(error);
            },
        );
  }


  createPayload(): IUpdatePaymentInfoRequest {
    return {
      creditCard: {
        cardNo: this.paymentInformationForm.controls.cardNumber.value,
        firstName:
            this.paymentInformationForm.controls.firstName.value.trim(),
        lastName:
            this.paymentInformationForm.controls.lastName.value.trim(),
        expiration:
            this.paymentInformationForm.controls.selectedMonth.value.toString().substring(0, 2) +
            this.paymentInformationForm.controls.selectedYear.value.toString().substring(2),
        cvv: this.paymentInformationForm.controls["CVV"].value,
      }
    };
  }

  cancel(): void {
    if(this.settingsService.settings.subscription.subscriptionStatus === ESubscriptionStatus.Inactive) this.authService.logout();
    else this.router.navigateByUrl("/settings");
  }

  get monthVector() {
    return monthVector;
  }

  protected readonly ValidationHelperService = ValidationHelperService;
}
